import { FC, useEffect } from 'react';
import { Space, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import { ICS2ArrivalView } from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import useICS2Codelists from 'hooks/useICS2Codelists';
import EllipsisTextTooltip from 'components/EllipsisTextTooltip';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import DeclarationStatusTag from 'components/ui/composed/declarations/declaration-status/DeclarationStatusTag';
import Mrn from './components/column-components/Mrn';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';

enum ArrivalNotificationColumnsTitles {
    MANIFEST_ID = 'Manifest ID',
    MRN = 'MRN',
    TRANSPORT_ID = 'Transport ID',
    ARRIVAL_DATE_AND_TIME = 'Arrival Date & Time',
    CUSTOMS_OFFICE = 'Customs Office',
    REGISTERED = 'Registered',
    STATUS = 'Status',
}

const DateTimeRow: FC<{ date: string | undefined; testId: string }> = ({ date, testId }) => {
    const momentDate = moment(date);

    if (!date || !momentDate.isValid()) {
        return <EmptyCell dataTestId={testId} />;
    }

    return <span data-testid={testId}>{momentDate.format('DD/MM/YY, HH:mm A')}</span>;
};

const EllipsisText: FC<{ text: string; testId: string }> = ({ text, testId }) => (
    <EllipsisTextTooltip tooltipTitle={text}>
        <span
            data-testid={testId}
            style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
            {text}
        </span>
    </EllipsisTextTooltip>
);

const CustomsOfficeRow: FC<{ reference: string | undefined }> = ({ reference }) => {
    const { getICS2CodeDescription, preloadICS2Codelists } = useICS2Codelists();

    useEffect(() => {
        preloadICS2Codelists([ICS2CodelistsMap.CL141]);
    }, [preloadICS2Codelists]);

    return (
        <EllipsisText
            text={getICS2CodeDescription(reference, ICS2CodelistsMap.CL141)}
            testId="arrival-notification-customs-office-row"
        />
    );
};

const ManifestReference: FC<{ reference: string | undefined; manifestId: string | undefined }> = ({
    reference,
    manifestId,
}) => {
    const testId = 'arrival-notification-manifest-id-row';

    if (!reference) return <EmptyCell dataTestId={testId} />;

    if (!manifestId) return <Typography.Text data-testid={testId}>{reference}</Typography.Text>;

    const to = `/manifests/${manifestId}/ens`;

    return (
        <Typography.Link
            data-testid={testId}
            onClick={(e) => e.stopPropagation()}
            href={to}
            style={{ whiteSpace: 'nowrap' }}
        >
            {reference}
        </Typography.Link>
    );
};

/**
 * A vessel is identified by its own number and an aircraft by the flight it is operating, so the
 * column shows whichever of the two the arrival carries.
 */
const transportId = (record: ICS2ArrivalView) =>
    record.data?.activeBorderTransportMeans?.identificationNumber ??
    record.data?.activeBorderTransportMeans?.conveyanceReferenceNumber;

/**
 * The standing IE3N07 and IE3N08 facts. They are shown beside the status because registration does
 * not clear them: an arrival can be registered and still have consignments under control.
 */
const StatusRow: FC<{ record: ICS2ArrivalView }> = ({ record }) => (
    <Space size={4} wrap>
        <DeclarationStatusTag
            status={record.status as unknown as DeclarationStatuses}
            dataTestId="arrival-notification-status-row"
        />
        {record.controlInstructions && (
            <Tag color="orange" data-testid="arrival-notification-controls-flag">
                Controls
            </Tag>
        )}
        {record.houseStateIssues && (
            <Tag color="red" data-testid="arrival-notification-house-issues-flag">
                House issues
            </Tag>
        )}
    </Space>
);

const getArrivalNotificationColumns = ({ showManifestId = true } = {}): ColumnsType<ICS2ArrivalView> => [
    ...(showManifestId
        ? [
              {
                  title: ArrivalNotificationColumnsTitles.MANIFEST_ID,
                  dataIndex: ArrivalNotificationColumnsTitles.MANIFEST_ID,
                  render: (text: string, record: ICS2ArrivalView) => (
                      <ManifestReference reference={record.data?.reference} manifestId={record?.sourceManifestId} />
                  ),
              },
          ]
        : []),
    {
        title: ArrivalNotificationColumnsTitles.MRN,
        dataIndex: ArrivalNotificationColumnsTitles.MRN,
        ellipsis: true,
        render: (text: string, record: ICS2ArrivalView) => (
            <Mrn mrn={record.arrivalMrn} dataTestId="arrival-notification-mrn-row" />
        ),
    },
    {
        title: ArrivalNotificationColumnsTitles.TRANSPORT_ID,
        dataIndex: ArrivalNotificationColumnsTitles.TRANSPORT_ID,
        render: (text: string, record: ICS2ArrivalView) => (
            <TooltipWithCopy dataTestId="arrival-notification-transport-id-row" copyText={transportId(record) || ''}>
                {transportId(record) || '-'}
            </TooltipWithCopy>
        ),
    },
    {
        title: ArrivalNotificationColumnsTitles.ARRIVAL_DATE_AND_TIME,
        dataIndex: ArrivalNotificationColumnsTitles.ARRIVAL_DATE_AND_TIME,
        ellipsis: true,
        render: (text: string, record: ICS2ArrivalView) => (
            <DateTimeRow
                date={record.actualArrivalAt ?? record.data?.activeBorderTransportMeans?.actualDateAndTimeOfArrival}
                testId="arrival-notification-arrival-date-row"
            />
        ),
    },
    {
        title: ArrivalNotificationColumnsTitles.CUSTOMS_OFFICE,
        dataIndex: ArrivalNotificationColumnsTitles.CUSTOMS_OFFICE,
        ellipsis: true,
        render: (text: string, record: ICS2ArrivalView) => (
            <CustomsOfficeRow reference={record.data?.actualCustomsOfficeOfFirstEntry?.referenceNumber} />
        ),
    },
    {
        title: ArrivalNotificationColumnsTitles.REGISTERED,
        dataIndex: ArrivalNotificationColumnsTitles.REGISTERED,
        ellipsis: true,
        render: (text: string, record: ICS2ArrivalView) => (
            <DateTimeRow date={record.registeredAt} testId="arrival-notification-registered-date-row" />
        ),
    },
    {
        title: ArrivalNotificationColumnsTitles.STATUS,
        dataIndex: ArrivalNotificationColumnsTitles.STATUS,
        width: 180,
        render: (text: string, record: ICS2ArrivalView) => <StatusRow record={record} />,
    },
];

export default getArrivalNotificationColumns;
